import { possessive } from '@shared';
import { Badge, Button } from '@design-system';

import { Scale, Sparkles, TrendingUp } from 'lucide-react';

import { type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useWizard } from 'react-use-wizard';
import { FreemiumPaidSuccess, useFreemiumGate } from '@/features/freemium-paywall';
import { useTopPolicyIrr } from '@/features/guided-financial-planning/hooks';
import { useGuidedPlanningFlatStepConfigs } from '@/features/guided-financial-planning/context';

import { selectClient } from '@/model';
import { getStepIndex } from '@/features/guided-financial-planning/config';

export function QuotesPaywallGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { goToStep } = useWizard();
  const flatStepConfigs = useGuidedPlanningFlatStepConfigs();
  const client = useSelector(selectClient);
  const clientId = client?.clientId ?? '';
  const clientFirstName = client?.clientFirstName || 'your client';

  const { isGated, isFinalizing, showPaidSuccess, dismissPaidSuccess } = useFreemiumGate(clientId);
  const { topIrr, selfFundingIrr } = useTopPolicyIrr(clientId, isGated && !isFinalizing);

  if (showPaidSuccess) {
    return (
      <FreemiumPaidSuccess
        clientId={clientId}
        clientFirstName={clientFirstName}
        onContinue={dismissPaidSuccess}
      />
    );
  }

  if (!isGated) {
    return <>{children}</>;
  }

  if (isFinalizing) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <div className="border-primary size-10 animate-spin rounded-full border-4 border-t-transparent" />
        <p className="text-foreground font-medium">Finalizing your purchase…</p>
      </div>
    );
  }

  function handleUnlock() {
    navigate(
      `/clients/${clientId}/onboarding/funding-sources?fxIntent=self-funding&fxStep=paywall`,
    );
  }

  function handleContinueSelfFunding() {
    goToStep(getStepIndex('plan-comparison', flatStepConfigs));
  }

  return (
    <div className="relative mx-auto w-full max-w-lg px-4 py-16 lg:py-24">
      <div className="mb-6 flex flex-col items-center gap-4 text-center">
        <Badge>
          <Sparkles />
          Optimized policies found
        </Badge>
        <h1 className="text-balance text-3xl font-bold text-gray-950">
          We found optimized policies for {clientFirstName}
        </h1>
        {topIrr ? (
          <p className="text-foreground max-w-md text-balance">
            Our top match projects a{' '}
            <span className="text-primary font-semibold">{topIrr} internal rate of return</span>
            {selfFundingIrr ? (
              <>
                , higher than the{' '}
                <span className="text-primary font-semibold">{selfFundingIrr}</span> IRR of{' '}
                {possessive(clientFirstName)} self-funding strategy.
              </>
            ) : (
              '.'
            )}
          </p>
        ) : (
          <p className="text-foreground max-w-md text-balance">
            We analyzed over a million policies and found coverage with a strong projected return on
            investment. Unlock to see exactly how it compares against self-funding.
          </p>
        )}

        <div className="text-foreground mt-2 flex flex-col gap-2 text-sm sm:flex-row sm:gap-6">
          <span className="inline-flex items-center justify-center gap-2">
            <TrendingUp className="text-primary size-4" />
            See the IRR, CAGR &amp; ROI
          </span>
          <span className="inline-flex items-center justify-center gap-2">
            <Scale className="text-primary size-4" />
            Compare against self-funding
          </span>
        </div>
      </div>

      <Button
        className="w-full"
        size="lg"
        onClick={handleUnlock}
      >
        Unlock optimized policies
      </Button>
      <Button
        className="mt-3 w-full"
        size="lg"
        variant="outline"
        onClick={handleContinueSelfFunding}
      >
        Continue with self-funding
      </Button>
    </div>
  );
}
